import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import {
  PostRequestPayload as CheckoutRequestPayload,
  PostResponsePayload as CheckoutResponsePayload,
  PARTNER_BOOKINGS_CHECKOUT_API,
  PartnerBookingSlotPayload,
} from 'constants/payloads/partnerBookingsCheckout';
import {
  PARTNER_BOOKINGS_QUOTE_API,
  PostRequestPayload as QuoteRequestPayload,
  PostResponsePayload as QuoteResponsePayload,
} from 'constants/payloads/partnerBookingsQuote';
import { useGetPartnerBookingOrderQuery } from 'types/generated/client';
import { detectFraudFromBrowser } from 'utils/shared/fraud/detectFraud';
import { useApiGateway } from 'hooks/useApi';
import { useModal } from 'hooks/useModal';
import { useViewer } from 'hooks/useViewer';
import Button from 'components/Button';
import LoadingSkeleton from 'components/LoadingSkeleton';
import Modal from 'components/modals/Modal';
import classNames from 'styles/utils/classNames';
import CourtList from './CourtList';
import DateStrip from './DateStrip';
import SlotGrid from './SlotGrid';
import { getIsOrderSettled, toBookingOutcome } from './orderStatus';
import { BookingAuthStep, BookingConfirmedStep, PayAndConfirmStep, SelectCourtStep } from './steps';
import { BookingQuote } from './types';
import { useCourtBooking } from './useCourtBooking';

interface Props {
  venueId: string;
  venueName: string;
  venueImageUrl?: string | null;
  timezone?: string | null;
  initialDate?: string;
  initialSlotId?: string;
  /** Fires when the booking flow takes over sign-in, so hosts can drop their own prompts. */
  onAuthStepOpen?: () => void;
  className?: string;
}

/** Which card the desktop column is showing; mobile drives the same steps as modals. */
type DesktopStep = 'SLOTS' | 'COURT' | 'AUTH' | 'PAY' | 'CONFIRMED';

const DEFAULT_PLAYER_COUNT = 2;

// One booking state drives both layouts, mounted together and switched with CSS. Each layout keeps
// its own step state: a modal portals out of the `lg:hidden` subtree, so a shared one would open both.
export default function BookCourtPanel({
  venueId,
  venueName,
  venueImageUrl,
  timezone,
  initialDate,
  initialSlotId,
  onAuthStepOpen,
  className,
}: Props) {
  const booking = useCourtBooking({ venueId, venueName, timezone, initialDate, initialSlotId });
  const { isAnonymousSession } = useViewer();
  const checkout = useApiGateway<CheckoutRequestPayload, CheckoutResponsePayload>();
  const quoteApi = useApiGateway<QuoteRequestPayload, QuoteResponsePayload>();

  const courtModal = useModal();
  const authModal = useModal();
  const payModal = useModal();
  const confirmedModal = useModal();
  const [desktopStep, setDesktopStep] = useState<DesktopStep>('SLOTS');
  /** Where the viewer was headed when the sign-in step interrupted them. */
  const [stepAfterAuth, setStepAfterAuth] = useState<'COURT' | 'PAY'>('COURT');
  const [quote, setQuote] = useState<BookingQuote | null>(null);
  const [order, setOrder] = useState<CheckoutResponsePayload | null>(null);

  // The booking settles minutes after checkout, once the club's system confirms it.
  const { data: orderData, stopPolling } = useGetPartnerBookingOrderQuery({
    variables: { id: order?.orderId ?? '' },
    skip: !order,
    pollInterval: 3000,
    onCompleted: (result) => {
      if (getIsOrderSettled(result.partnerBookingOrdersByPk?.status)) {
        stopPolling();
      }
    },
  });
  const outcome = toBookingOutcome(orderData?.partnerBookingOrdersByPk?.status ?? order?.status);

  const getSlotPayload = (): PartnerBookingSlotPayload | null => {
    if (!booking.selectedCourt || !booking.startLocal || booking.selectedMinutes <= 0) {
      return null;
    }
    return {
      venueId,
      providerResourceId: booking.selectedCourt.providerResourceId,
      providerCourtId: booking.selectedCourt.id,
      localDate: booking.selectedDate,
      startLocal: booking.startLocal,
      durationMinutes: booking.selectedMinutes,
      playerCount: DEFAULT_PLAYER_COUNT,
    };
  };

  /** Prices the stretch on the server, exactly as checkout will charge it. */
  const requestQuote = async () => {
    const slot = getSlotPayload();
    if (!slot) {
      return;
    }
    setQuote(null);
    const response = await quoteApi.post({ endpoint: PARTNER_BOOKINGS_QUOTE_API, payload: slot });
    if (response.isError || !response.data || typeof response.data === 'string') {
      toast.error(response.error || 'Could not price this booking');
      return;
    }
    setQuote({
      rentalUnitAmount: response.data.subtotalUnitAmount,
      listRentalUnitAmount: null,
      feesUnitAmount: response.data.feeUnitAmount,
      totalUnitAmount: response.data.totalUnitAmount,
      currency: response.data.currency,
    });
  };

  const startAuthStep = (next: 'COURT' | 'PAY') => {
    setStepAfterAuth(next);
    onAuthStepOpen?.();
  };

  /** Mobile: only one modal is open at a time, so the current step closes first. */
  const openAuthModal = (next: 'COURT' | 'PAY', closeCurrent?: () => void) => {
    startAuthStep(next);
    closeCurrent?.();
    authModal.openModal();
  };

  const openPayModal = () => {
    if (isAnonymousSession) {
      openAuthModal('PAY', courtModal.closeModal);
      return;
    }
    courtModal.closeModal();
    payModal.openModal();
    requestQuote();
  };

  const handleAuthModalComplete = () => {
    authModal.closeModal();
    if (stepAfterAuth === 'PAY') {
      payModal.openModal();
      requestQuote();
      return;
    }
    courtModal.openModal();
  };

  /** Desktop: the same transitions, swapping the card in the column instead. */
  const goToDesktopStep = (next: 'COURT' | 'PAY') => {
    if (isAnonymousSession) {
      startAuthStep(next);
      setDesktopStep('AUTH');
      return;
    }
    setDesktopStep(next);
    if (next === 'PAY') {
      requestQuote();
    }
  };

  const handleDesktopAuthComplete = () => {
    setDesktopStep(stepAfterAuth);
    if (stepAfterAuth === 'PAY') {
      requestQuote();
    }
  };

  /** Holds the court and authorizes the saved card; the bot is sent only after that. */
  const handlePay = async (paymentMethodId: string) => {
    const slot = getSlotPayload();
    if (!slot) {
      return false;
    }
    const fraud = detectFraudFromBrowser();
    const response = await checkout.post({
      endpoint: PARTNER_BOOKINGS_CHECKOUT_API,
      payload: {
        ...slot,
        paymentMethodId,
        clientTimezone: fraud.timezone,
        clientLocale: fraud.locale,
      },
    });
    if (response.isError || !response.data || typeof response.data === 'string') {
      toast.error(response.error || 'Could not create the booking');
      return false;
    }
    setOrder(response.data);
    return true;
  };

  const handleMobilePay = async (paymentMethodId: string) => {
    if (await handlePay(paymentMethodId)) {
      payModal.closeModal();
      confirmedModal.openModal();
    }
  };

  const handleDesktopPay = async (paymentMethodId: string) => {
    if (await handlePay(paymentMethodId)) {
      setDesktopStep('CONFIRMED');
    }
  };

  const handleBookMore = () => {
    confirmedModal.closeModal();
    setDesktopStep('SLOTS');
    setOrder(null);
    setQuote(null);
    booking.reset();
  };

  const grid = (columns: 3 | 4) => (
    <SlotGrid
      columns={columns}
      slots={booking.slots}
      selectedSlotIds={booking.selectedSlotIds}
      onSlotSelect={booking.toggleSlot}
      minimumMinutes={booking.availability.minimumMinutes}
      slotMinutes={booking.availability.slotMinutes}
    />
  );

  const notice = booking.hasFailed
    ? "We couldn't load this club's availability. Please try again."
    : !booking.isLoading && !booking.isBookable
    ? "This club isn't taking bookings through Bounce yet."
    : !booking.isLoading && booking.availability.dates.length === 0
    ? `No days open to book yet. This club takes Bounce bookings ${booking.availability.bookableHoursLabel}.`
    : null;

  const body = (columns: 3 | 4) =>
    booking.isLoading && booking.slots.length === 0 ? (
      <LoadingSkeleton count={4} />
    ) : notice ? (
      <p className="typography-product-body text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
        {notice}
      </p>
    ) : (
      grid(columns)
    );

  const dateStrip = (
    <DateStrip
      dates={booking.availability.dates}
      selectedDate={booking.selectedDate}
      onDateChange={booking.changeDate}
      size="lg"
    />
  );

  const dateLabel = booking.selectedDate
    ? format(parseISO(booking.selectedDate), 'MMM d • EEE')
    : '';

  const payStepProps = {
    venueName,
    venueImageUrl,
    court: booking.selectedCourt,
    quote,
    isQuoteLoading: quoteApi.isLoading,
    dateLabel,
    timeLabel: booking.timeLabel,
    isPaying: checkout.isLoading,
  };

  return (
    <div className={className}>
      {/* Mobile: the whole flow reads as one column under the club header. */}
      <div className="flex flex-col gap-ds-lg lg:hidden">
        {dateStrip}
        <span className="h-px w-full bg-color-border-card-lightmode dark:bg-color-border-card-darkmode" />
        {body(3)}
        <span className="h-px w-full bg-color-border-card-lightmode dark:bg-color-border-card-darkmode" />
        <CourtList
          courts={booking.availability.courts}
          selectedCourtId={booking.selectedCourtId}
          onCourtSelect={booking.selectCourt}
        />
        <Button
          variant="brand"
          size="lg"
          label="Book now"
          disabled={!booking.isSelectionValid || !booking.selectedCourtId}
          onClick={openPayModal}
        />
      </div>

      {/* Desktop: a standalone card in the page's right column, which each step
          takes over in turn. */}
      <div
        className={classNames(
          'hidden flex-col rounded-2xl border border-color-border-card-lightmode bg-color-bg-lightmode-primary shadow-feed-card lg:flex',
          'dark:border-color-border-card-darkmode dark:bg-color-bg-darkmode-primary',
        )}
      >
        {desktopStep === 'SLOTS' && (
          <div className="flex flex-col gap-ds-lg p-ds-xl">
            <p className="typography-product-heading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              Book a court
            </p>
            {dateStrip}
            <span className="h-px w-full bg-color-border-card-lightmode dark:bg-color-border-card-darkmode" />
            {body(4)}
            <span className="h-px w-full bg-color-border-card-lightmode dark:bg-color-border-card-darkmode" />
            <Button
              variant="brand"
              size="lg"
              label="Select"
              disabled={!booking.isSelectionValid}
              onClick={() => goToDesktopStep('COURT')}
            />
          </div>
        )}

        {desktopStep === 'COURT' && (
          <SelectCourtStep
            onBack={() => setDesktopStep('SLOTS')}
            courts={booking.availability.courts}
            selectedCourtId={booking.selectedCourtId}
            onCourtSelect={booking.selectCourt}
            dateLabel={booking.dateLabel}
            timeLabel={booking.timeLabel}
            onConfirm={() => goToDesktopStep('PAY')}
          />
        )}

        {desktopStep === 'AUTH' && (
          <BookingAuthStep
            onBack={() => setDesktopStep(stepAfterAuth === 'PAY' ? 'COURT' : 'SLOTS')}
            onAuthComplete={handleDesktopAuthComplete}
          />
        )}

        {desktopStep === 'PAY' && (
          <PayAndConfirmStep
            {...payStepProps}
            onBack={() => setDesktopStep('COURT')}
            onPay={handleDesktopPay}
          />
        )}

        {desktopStep === 'CONFIRMED' && (
          <BookingConfirmedStep outcome={outcome} onBookMore={handleBookMore} />
        )}
      </div>

      {/* Mobile presents the same steps as modals. */}
      <Modal
        isOpen={authModal.isOpen}
        handleClose={authModal.closeModal}
        classNameMaxWidth="sm:max-w-[34.8125rem]"
      >
        <BookingAuthStep
          onBack={authModal.closeModal}
          onAuthComplete={handleAuthModalComplete}
          className="max-h-[85dvh]"
        />
      </Modal>

      <Modal
        isOpen={courtModal.isOpen}
        handleClose={courtModal.closeModal}
        classNameMaxWidth="sm:max-w-[34.8125rem]"
      >
        <SelectCourtStep
          onBack={courtModal.closeModal}
          courts={booking.availability.courts}
          selectedCourtId={booking.selectedCourtId}
          onCourtSelect={booking.selectCourt}
          dateLabel={booking.dateLabel}
          timeLabel={booking.timeLabel}
          onConfirm={openPayModal}
          className="max-h-[85dvh]"
        />
      </Modal>

      <Modal
        isOpen={payModal.isOpen}
        handleClose={payModal.closeModal}
        classNameMaxWidth="sm:max-w-[34.8125rem]"
      >
        {payModal.isOpen && (
          <PayAndConfirmStep
            {...payStepProps}
            onBack={() => {
              payModal.closeModal();
              courtModal.openModal();
            }}
            onPay={handleMobilePay}
            className="max-h-[85dvh]"
          />
        )}
      </Modal>

      <Modal
        isOpen={confirmedModal.isOpen}
        handleClose={handleBookMore}
        classNameMaxWidth="sm:max-w-md"
      >
        <BookingConfirmedStep outcome={outcome} onBookMore={handleBookMore} />
      </Modal>
    </div>
  );
}
